import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Target,
  CheckCircle2,
  Clock,
  TrendingDown,
  TrendingUp,
  Minus,
  Lightbulb,
  Quote,
  Mail,
  Linkedin,
} from "lucide-react";
import Navbar from "../Navbar";
import { Footer } from "../CTAFooter";
import { successStories, SITE } from "../../data/mock";

const BASE_URL = "https://abhinavjoshi.com";

// Creates the tag if it doesn't exist yet, and restores/removes it on cleanup
// so navigating away doesn't leave stale tags for the next page.
const upsertMeta = (attr, key, content) => {
  let tag = document.querySelector(`meta[${attr}="${key}"]`);
  const created = !tag;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  const prevContent = tag.getAttribute("content");
  tag.setAttribute("content", content);
  return { tag, created, prevContent };
};

const mono = { fontFamily: "'Roboto Mono', monospace" };

const Badge = ({ children, light = false }) => (
  <span
    style={mono}
    className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] px-3.5 py-1.5 rounded-full border ${
      light
        ? "bg-white text-[#000d14] border-transparent"
        : "bg-neutral-50 text-neutral-500 border-black/10"
    }`}
  >
    {children}
  </span>
);

const InfoCard = ({ label, value }) => (
  <div className="border border-black/10 rounded-2xl px-4 py-3.5 bg-neutral-50">
    <div style={mono} className="text-[11px] uppercase tracking-[0.05em] text-neutral-500">
      {label}
    </div>
    <div className="text-sm font-medium mt-1 text-neutral-900">{value}</div>
  </div>
);

const PillItem = ({ icon: Icon, iconColor, rounded = false, children }) => (
  <div
    className={`flex items-start gap-2.5 border border-black/10 bg-white px-3.5 ${
      rounded ? "py-2.5 rounded-full w-fit" : "py-2.5 rounded-xl"
    }`}
  >
    <Icon size={15} className="flex-shrink-0 mt-0.5" style={{ color: iconColor }} />
    <span className="text-[13.5px] text-neutral-700 leading-relaxed">{children}</span>
  </div>
);

const Chip = ({ children }) => (
  <span className="text-[13px] text-neutral-700 border border-black/10 rounded-full px-3.5 py-1.5 bg-neutral-50">
    {children}
  </span>
);

// Inline SVG line chart, no charting dependency needed.
const EvidenceChart = ({ labels, data, datasetLabel }) => {
  const width = 600;
  const height = 260;
  const padX = 36;
  const padY = 28;
  const max = Math.max(...data) * 1.15;
  const stepX = (width - padX * 2) / (data.length - 1);

  const points = data.map((val, i) => ({
    x: padX + i * stepX,
    y: height - padY - (val / max) * (height - padY * 2),
    val,
  }));

  const path = points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(" ");
  const areaPath = `${path} L ${points[points.length - 1].x} ${height - padY} L ${points[0].x} ${height - padY} Z`;

  return (
    <div className="border border-black/10 rounded-3xl p-4 bg-white mt-4">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
        <defs>
          <linearGradient id="evidenceFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#000d14" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#000d14" stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1={padX} y1={height - padY} x2={width - padX} y2={height - padY} stroke="#e5e5e5" />
        <path d={areaPath} fill="url(#evidenceFill)" />
        <path d={path} fill="none" stroke="#000d14" strokeWidth="2.5" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="4" fill="#000d14" />
            <text x={p.x} y={p.y - 14} textAnchor="middle" fontSize="12" fontWeight="600" fill="#000d14">
              ₹{p.val}L
            </text>
            <text x={p.x} y={height - padY + 20} textAnchor="middle" fontSize="11" fill="#737373">
              {labels[i]}
            </text>
          </g>
        ))}
      </svg>
      <p className="text-xs text-neutral-400 mt-2">{datasetLabel}</p>
    </div>
  );
};

const trendMeta = (trend) => {
  if (trend === "down-good") return { Icon: TrendingDown, color: "#059669" };
  if (trend === "up-good") return { Icon: TrendingUp, color: "#059669" };
  return { Icon: Minus, color: "#a3a3a3" };
};

export default function SuccessStoryDetail() {
  const { slug } = useParams();
  const story = successStories.find((s) => s.slug === slug);

  // ---- Sticky nav + scroll-spy setup ----
  const sectionDefs = story
    ? [
        { id: "s-overview", label: "Client overview", show: true },
        { id: "s-challenge", label: "Challenge", show: true },
        { id: "s-approach", label: "Approach", show: true },
        { id: "s-timeline", label: "Timeline", show: true },
        { id: "s-evidence", label: "Evidence", show: !!story.evidenceChart },
        { id: "s-lessons", label: "Lessons learned", show: story.lessonsLearned?.length > 0 },
        { id: "s-testimonial", label: "Testimonial", show: !!story.testimonial?.quote },
        { id: "s-roi", label: "ROI", show: !!story.roi?.summary },
        { id: "s-next", label: "Next steps", show: story.nextSteps?.length > 0 },
        { id: "s-notes", label: "Methodology", show: !!story.appendix?.content },
      ].filter((s) => s.show)
    : [];

  const [active, setActive] = useState(sectionDefs[0]?.id);
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!story) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-40% 0px -50% 0px" }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.slug]);

  // ---- Per-page SEO: title, meta description, canonical, Open Graph, Twitter card ----
  useEffect(() => {
    if (!story) return;

    const prevTitle = document.title;
    const metaTitle = story.seo?.metaTitle || `${story.listingTitle} | ${SITE.name}`;
    const metaDescription = story.seo?.metaDescription || story.listingSummary;
    const pageUrl = `${BASE_URL}/success-stories/${story.slug}`;
    const imageUrl = story.bannerImage?.startsWith("http")
      ? story.bannerImage
      : `${BASE_URL}${story.bannerImage || ""}`;

    document.title = `${metaTitle} | ${SITE.name}`;

    const restores = [
      upsertMeta("name", "description", metaDescription),
      upsertMeta("property", "og:title", metaTitle),
      upsertMeta("property", "og:description", metaDescription),
      upsertMeta("property", "og:image", imageUrl),
      upsertMeta("property", "og:type", "article"),
      upsertMeta("property", "og:url", pageUrl),
      upsertMeta("name", "twitter:card", "summary_large_image"),
      upsertMeta("name", "twitter:title", metaTitle),
      upsertMeta("name", "twitter:description", metaDescription),
      upsertMeta("name", "twitter:image", imageUrl),
    ];

    let canonical = document.querySelector('link[rel="canonical"]');
    const createdCanonical = !canonical;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    const prevCanonicalHref = canonical.getAttribute("href");
    canonical.setAttribute("href", pageUrl);

    return () => {
      document.title = prevTitle;
      restores.forEach(({ tag, created, prevContent }) => {
        if (created) {
          tag.remove();
        } else if (prevContent !== null) {
          tag.setAttribute("content", prevContent);
        }
      });
      if (createdCanonical) {
        canonical.remove();
      } else if (prevCanonicalHref) {
        canonical.setAttribute("href", prevCanonicalHref);
      }
    };
  }, [story]);

  const scrollTo = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!story) {
    return (
      <div className="min-h-screen bg-white font-sans text-neutral-900">
        <Navbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
          <h1 className="text-2xl font-semibold text-neutral-900 mb-2">This case study isn't ready yet</h1>
          <p className="text-neutral-500 mb-6">We couldn't find a success story at this address.</p>
          <Link
            to="/success-stories"
            className="inline-flex items-center gap-2 rounded-full bg-[#000d14] text-white px-5 py-2.5 text-sm font-medium"
          >
            <ArrowLeft size={16} />
            Back to success stories
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-white font-sans text-neutral-900">
      <Navbar />

      {/* ---- Hero: full-bleed blurred banner ---- */}
      <div className="relative w-full h-[420px] overflow-hidden bg-[#000d14]">
        <img
          src={story.bannerImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover blur-[9px] scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/50" />
        <div className="relative z-10 max-w-[1280px] mx-auto px-8 h-full flex flex-col items-start justify-end pb-16">
          <Badge light>
            {story.clientName} · {story.tag}
          </Badge>
          <h1 className="text-white font-semibold tracking-[-0.02em] leading-[1.1] mt-5 text-[28px] sm:text-[40px] lg:text-[44px] max-w-3xl">
            {story.listingTitle}
          </h1>
          <p className="text-white/85 mt-3.5 text-[15px] max-w-2xl">{story.listingSummary}</p>
        </div>
      </div>

      {/* ---- Four highlight cards ---- */}
      {story.highlights && (
        <section className="pt-10 pb-8">
          <div className="max-w-[1280px] mx-auto px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-neutral-50 border border-black/5 rounded-3xl p-6">
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                  <Target size={16} style={{ color: "#d97706" }} />
                </div>
                <h3 className="mt-3.5 text-sm font-semibold">Challenge</h3>
                <div className="mt-1.5 text-[13px] text-neutral-500 leading-relaxed">
                  {story.highlights.challenge}
                </div>
              </div>
              <div className="bg-neutral-50 border border-black/5 rounded-3xl p-6">
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                  <CheckCircle2 size={16} style={{ color: "#059669" }} />
                </div>
                <h3 className="mt-3.5 text-sm font-semibold">Approach</h3>
                <div className="mt-1.5 text-[13px] text-neutral-500 leading-relaxed">
                  {story.highlights.approach}
                </div>
              </div>
              <div className="bg-neutral-50 border border-black/5 rounded-3xl p-6">
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                  <Clock size={16} style={{ color: "#0891b2" }} />
                </div>
                <h3 className="mt-3.5 text-sm font-semibold">Timeline</h3>
                <div className="mt-1.5 text-[13px] text-neutral-500 leading-relaxed">
                  {story.highlights.timeline}
                </div>
              </div>
              <div className="bg-neutral-50 border border-black/5 rounded-3xl p-6">
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                  <TrendingUp size={16} style={{ color: "#059669" }} />
                </div>
                <h3 className="mt-3.5 text-sm font-semibold">Result</h3>
                <div className="mt-1.5 text-[13px] text-neutral-500 leading-relaxed">
                  {story.highlights.result}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="h-px bg-black/[0.06] max-w-[1280px] mx-auto" />

      {/* ---- Sticky nav + connected sections ---- */}
      <section className="py-16">
        <div className="max-w-[1280px] mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-12">
            {/* Sticky in-page nav */}
            <nav className="flex md:flex-col gap-1 md:gap-1 overflow-x-auto md:overflow-visible md:sticky md:top-24 md:self-start pb-2">
              {sectionDefs.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`text-left whitespace-nowrap px-3.5 py-2.5 rounded-[10px] text-[13.5px] transition-colors ${
                    active === s.id
                      ? "bg-neutral-50 text-neutral-900 font-medium"
                      : "text-neutral-500 hover:text-neutral-900"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </nav>

            {/* Content */}
            <div>
              {/* Client overview */}
              <div
                id="s-overview"
                ref={(el) => (sectionRefs.current["s-overview"] = el)}
                className="py-9 border-b border-black/5 scroll-mt-24"
              >
                <Badge>Client</Badge>
                <h2 className="text-[22px] font-semibold tracking-[-0.02em] mt-2.5">About {story.clientName}</h2>
                <p className="text-neutral-500 leading-relaxed mt-3">{story.clientOverview.about}</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  <InfoCard label="Industry" value={story.clientOverview.industry} />
                  <InfoCard label="Scale" value={story.clientOverview.scale} />
                </div>
              </div>

              {/* Challenge */}
              <div
                id="s-challenge"
                ref={(el) => (sectionRefs.current["s-challenge"] = el)}
                className="py-9 border-b border-black/5 scroll-mt-24"
              >
                <Badge>Challenge</Badge>
                <h2 className="text-[22px] font-semibold tracking-[-0.02em] mt-2.5">The problem going in</h2>
                <p className="text-neutral-500 leading-relaxed mt-3">{story.challenge.summary}</p>
                <div className="flex flex-col gap-2 mt-4">
                  {story.challenge.points.map((point, i) => (
                    <PillItem key={i} icon={Target} iconColor="#d97706">
                      {point}
                    </PillItem>
                  ))}
                </div>
              </div>

              {/* Approach */}
              <div
                id="s-approach"
                ref={(el) => (sectionRefs.current["s-approach"] = el)}
                className="py-9 border-b border-black/5 scroll-mt-24"
              >
                <Badge>Approach</Badge>
                <h2 className="text-[22px] font-semibold tracking-[-0.02em] mt-2.5">What we did</h2>
                <p className="text-neutral-500 leading-relaxed mt-3">{story.solution.summary}</p>
                <div className="flex flex-col gap-2 mt-4">
                  {story.solution.strategies.map((s, i) => (
                    <PillItem key={i} icon={CheckCircle2} iconColor="#059669">
                      {s}
                    </PillItem>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-3.5">
                  {story.solution.platforms.map((p, i) => (
                    <Chip key={i}>{p}</Chip>
                  ))}
                </div>
              </div>

              {/* Timeline */}
              <div
                id="s-timeline"
                ref={(el) => (sectionRefs.current["s-timeline"] = el)}
                className="py-9 border-b border-black/5 scroll-mt-24"
              >
                <Badge>Timeline</Badge>
                <h2 className="text-[22px] font-semibold tracking-[-0.02em] mt-2.5">Rollout</h2>
                <p className="text-neutral-500 leading-relaxed mt-3">{story.implementation.timeline}</p>
                <div className="mt-4">
                  {story.implementation.milestones.map((m, i) => (
                    <div key={i} className="flex gap-3.5">
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className="w-[9px] h-[9px] rounded-full bg-neutral-900 mt-1.5" />
                        {i < story.implementation.milestones.length - 1 && (
                          <div className="w-px flex-1 bg-black/10 mt-1" />
                        )}
                      </div>
                      <div className="pb-[18px]">
                        <div className="text-[13.5px] font-semibold">{m.phase}</div>
                        <div className="text-[13.5px] text-neutral-500 mt-0.5">{m.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-neutral-500 leading-relaxed mt-4">{story.implementation.methodology}</p>
              </div>

              {/* Evidence */}
              {story.evidenceChart && (
                <div
                  id="s-evidence"
                  ref={(el) => (sectionRefs.current["s-evidence"] = el)}
                  className="py-9 border-b border-black/5 scroll-mt-24"
                >
                  <Badge>Evidence</Badge>
                  <h2 className="text-[22px] font-semibold tracking-[-0.02em] mt-2.5">How this was measured</h2>
                  <EvidenceChart {...story.evidenceChart} />
                  {story.evidenceNote && (
                    <p className="text-sm text-neutral-500 leading-relaxed mt-3">{story.evidenceNote}</p>
                  )}
                </div>
              )}

              {/* Lessons learned */}
              {story.lessonsLearned?.length > 0 && (
                <div
                  id="s-lessons"
                  ref={(el) => (sectionRefs.current["s-lessons"] = el)}
                  className="py-9 border-b border-black/5 scroll-mt-24"
                >
                  <Badge>Along the way</Badge>
                  <h2 className="text-[22px] font-semibold tracking-[-0.02em] mt-2.5">What didn't go perfectly</h2>
                  <div className="mt-4 flex flex-col gap-3">
                    {story.lessonsLearned.map((l, i) => (
                      <div key={i} className="border border-black/10 rounded-2xl p-4 bg-white">
                        <div className="flex items-start gap-2.5">
                          <Lightbulb size={15} className="flex-shrink-0 mt-0.5 text-neutral-400" />
                          <div>
                            <div className="text-[13.5px] font-medium">{l.challenge}</div>
                            <div className="text-[13px] text-neutral-500 mt-1 leading-relaxed">{l.resolution}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Testimonial */}
              {story.testimonial?.quote && (
                <div
                  id="s-testimonial"
                  ref={(el) => (sectionRefs.current["s-testimonial"] = el)}
                  className="py-9 border-b border-black/5 scroll-mt-24"
                >
                  <Badge>In their words</Badge>
                  <div className="border border-black/10 rounded-3xl p-8 bg-neutral-50 mt-4">
                    <Quote size={20} className="text-neutral-300" />
                    <blockquote className="text-lg font-medium tracking-[-0.01em] leading-snug mt-3">
                      "{story.testimonial.quote}"
                    </blockquote>
                    <div className="flex items-center gap-2.5 mt-4">
                      <div className="w-9 h-9 rounded-full bg-neutral-200 flex items-center justify-center text-[13px] font-semibold text-neutral-500">
                        {story.testimonial.name?.[0]}
                      </div>
                      <div>
                        <div className="text-[13.5px] font-semibold">{story.testimonial.name}</div>
                        <div className="text-xs text-neutral-500">{story.testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ROI */}
              {story.roi?.summary && (
                <div
                  id="s-roi"
                  ref={(el) => (sectionRefs.current["s-roi"] = el)}
                  className="py-9 border-b border-black/5 scroll-mt-24"
                >
                  <Badge>Business value</Badge>
                  <h2 className="text-[22px] font-semibold tracking-[-0.02em] mt-2.5">Return on investment</h2>
                  <p className="text-neutral-500 leading-relaxed mt-3">{story.roi.summary}</p>
                  {story.roi.paybackPeriod && (
                    <div className="inline-flex items-center gap-2.5 border border-black/10 rounded-[10px] px-3.5 py-2.5 bg-neutral-50 mt-3.5">
                      <span style={mono} className="text-[11px] uppercase text-neutral-500">
                        Payback period
                      </span>
                      <span className="text-[13.5px] font-semibold">{story.roi.paybackPeriod}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Next steps */}
              {story.nextSteps?.length > 0 && (
                <div
                  id="s-next"
                  ref={(el) => (sectionRefs.current["s-next"] = el)}
                  className="py-9 border-b border-black/5 scroll-mt-24"
                >
                  <Badge>What's next</Badge>
                  <h2 className="text-[22px] font-semibold tracking-[-0.02em] mt-2.5">Future steps</h2>
                  <div className="flex flex-col gap-2 mt-4">
                    {story.nextSteps.map((step, i) => (
                      <PillItem key={i} icon={ArrowUpRight} iconColor="#171717" rounded>
                        {step}
                      </PillItem>
                    ))}
                  </div>
                </div>
              )}

              {/* Methodology & data notes */}
              {story.appendix?.content && (
                <div
                  id="s-notes"
                  ref={(el) => (sectionRefs.current["s-notes"] = el)}
                  className="py-9 scroll-mt-24"
                >
                  <Badge>Notes</Badge>
                  <h2 className="text-[22px] font-semibold tracking-[-0.02em] mt-2.5">
                    {story.appendix.title || "Methodology & data notes"}
                  </h2>
                  <p className="text-neutral-500 leading-relaxed mt-3">{story.appendix.content}</p>
                </div>
              )}
            </div>
          </div>
        </div>

      </section>

      {/* ---- Metrics band ---- */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-[1280px] mx-auto px-8">
          <Badge>Measurable impact</Badge>
          <h2 className="text-[30px] font-semibold tracking-[-0.02em] mt-4 max-w-lg">The headline numbers</h2>
          <div
            className={`grid gap-4 sm:gap-6 mt-9 text-center ${
              story.results.metrics.length >= 5
                ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
                : "grid-cols-2 lg:grid-cols-4"
            }`}
          >
            {story.results.metrics.map((metric, i) => {
              const { Icon, color } = trendMeta(metric.trend);
              return (
                <div key={i} className="bg-white border border-black/5 rounded-3xl px-3 sm:px-4 py-6">
                  <div className="flex items-center justify-center gap-1.5">
                    <Icon size={18} style={{ color }} />
                    <span
                      className={`font-semibold tracking-[-0.02em] ${
                        story.results.metrics.length >= 5 ? "text-[24px] sm:text-[28px]" : "text-[32px]"
                      }`}
                    >
                      {metric.value}
                    </span>
                  </div>
                  <div className="text-[13px] text-neutral-500 mt-2">{metric.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---- CTA banner ---- */}
      {story.cta && (
        <div className="max-w-[1280px] mx-auto px-8 py-16">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src={story.bannerImage}
              alt=""
              className="absolute inset-0 w-full h-full object-cover blur-[9px] scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/50" />
            <div className="relative z-10 max-w-[1020px] mx-auto flex flex-wrap items-center justify-between gap-6 px-6 sm:px-8 py-7">
              <div className="max-w-[560px]">
                <h2 className="text-white text-[26px] sm:text-[32px] font-semibold tracking-[-0.02em] leading-tight">
                  {story.cta.headline}
                </h2>
                <p className="text-white/80 mt-3.5 text-[15px] leading-relaxed">{story.cta.body}</p>
              </div>
              <div className="flex flex-col items-start sm:items-end gap-4">
                <Link
                  to={story.href || "/connect"}
                  className="inline-flex items-center gap-2 bg-white text-[#000d14] font-medium text-[14.5px] px-[18px] py-[11px] rounded-xl shadow-[0_0_0_5px_rgba(255,255,255,0.15)] whitespace-nowrap"
                >
                  Book a strategy call
                  <ArrowUpRight size={15} />
                </Link>
                <div className="flex items-center gap-4 text-white/90 text-[13.5px] flex-wrap">
                  <a href={`mailto:${SITE.email}`} className="flex items-center gap-1.5 hover:text-white">
                    <Mail size={15} /> {SITE.email}
                  </a>
                  <a href={SITE.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-white">
                    <Linkedin size={15} /> LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
